import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";

interface UseCaseHeroProps {
  badge: string;
  title: string;
  subtitle: string;
  description: string;
  icon: LucideIcon;
  accentColor: string;
  personaInitials: string;
  personaName: string;
  personaRole: string;
  personaCity: string;
}

const UseCaseHero = ({
  badge,
  title,
  subtitle,
  description,
  icon: Icon,
  accentColor,
  personaInitials,
  personaName,
  personaRole,
  personaCity,
}: UseCaseHeroProps) => {
  return (
    <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-[#0A0A0F] pt-24">
      {/* Gradient mesh background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
        <div
          className="absolute top-0 right-1/4 w-[600px] h-[600px] rounded-full blur-[150px] opacity-20"
          style={{ backgroundColor: accentColor }}
        />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
      </div>

      {/* Subtle grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

      <div className="container relative z-10 mx-auto px-4 py-16">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          {/* Content Left */}
          <div className="space-y-8 max-w-2xl">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-white/40 animate-fade-up">
              <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
              <span>/</span>
              <Link to="/use-cases" className="hover:text-white transition-colors">Cas d'usage</Link>
              <span>/</span>
              <span className="text-white/60">{badge}</span>
            </nav>

            {/* Badge */}
            <div
              className="inline-flex items-center gap-2.5 rounded-full border px-4 py-2 backdrop-blur-sm animate-fade-up"
              style={{
                borderColor: `${accentColor}30`,
                backgroundColor: `${accentColor}10`,
              }}
            >
              <Icon className="h-4 w-4" style={{ color: accentColor }} />
              <span className="text-sm font-medium uppercase tracking-wider" style={{ color: accentColor }}>
                {badge}
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="animate-fade-up [animation-delay:100ms]">
              <span className="block text-[2.5rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
                {title}
              </span>
              <span
                className="block text-[2.5rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mt-2"
                style={{ color: accentColor }}
              >
                {subtitle}
              </span>
            </h1>

            {/* Description */}
            <p className="text-lg sm:text-xl text-white/50 leading-relaxed max-w-lg animate-fade-up [animation-delay:200ms]">
              {description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-up [animation-delay:300ms]">
              <Button
                size="lg"
                className="h-12 px-6 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base gap-2.5 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_8px_30px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
                asChild
              >
                <Link to="/auth?mode=signup">
                  Essayer gratuitement 14 jours
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Persona Card Right */}
          <div className="relative animate-fade-up [animation-delay:200ms] lg:animate-slide-in-right">
            {/* Glow behind */}
            <div
              className="absolute -inset-8 blur-3xl rounded-3xl opacity-20"
              style={{ backgroundColor: accentColor }}
            />

            {/* Main card */}
            <div className="relative rounded-3xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
              <div className="rounded-3xl bg-[#0F0F14]/90 backdrop-blur-xl overflow-hidden p-8">
                {/* Persona avatar */}
                <div className="flex flex-col items-center text-center mb-8">
                  <div
                    className="h-24 w-24 rounded-2xl flex items-center justify-center text-3xl font-bold text-white mb-4 shadow-2xl"
                    style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}80)` }}
                  >
                    {personaInitials}
                  </div>
                  <h3 className="text-xl font-bold text-white">{personaName}</h3>
                  <p className="text-white/60">{personaRole}</p>
                  <p className="text-white/40 text-sm">{personaCity}</p>
                </div>

                {/* Icon illustration */}
                <div className="flex justify-center">
                  <div
                    className="h-32 w-32 rounded-2xl flex items-center justify-center opacity-20"
                    style={{ backgroundColor: accentColor }}
                  >
                    <Icon className="h-16 w-16 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCaseHero;
